"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { LaunchHandoffRoot } from "@/components/launch/LaunchHandoffRoot";
import { OutOfCreditsDialog } from "@/components/billing/OutOfCreditsDialog";
import { ProtectedRoute } from "@/components/protected-route";
import { SetupGate } from "@/components/setup/SetupGate";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useIsWorkbenchRoute } from "@/hooks/useNavigationData";
import { useDeferredShellUi } from "@/hooks/useDeferredShellUi";
import { cn } from "@/lib/utils";
import { useCreditStore } from "@/stores/useCreditStore";
import { useCurrentUserSharesStore } from "@/stores/useCurrentUserSharesStore";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
function DashboardShellContent(t0) {
  const $ = _c(34);
  const {
    children
  } = t0;
  const isWorkbenchRoute = useIsWorkbenchRoute();
  const [standardSidebarOpen, setStandardSidebarOpen] = useState(true);
  const [workbenchSidebarOpen, setWorkbenchSidebarOpen] = useState(false);
  const sidebarOpen = isWorkbenchRoute ? workbenchSidebarOpen : standardSidebarOpen;
  const sidebarShellRef = useRef(null);
  const showDeferredUi = useDeferredShellUi();
  const fetchSharesForCurrentUser = useCurrentUserSharesStore(_temp);
  const currentWorkspaceId = useWorkspaceStore(_temp2);
  const fetchCreditBalance = useCreditStore(_temp3);
  const fetchBillingProfileStatus = useCreditStore(_temp4);
  const lastCreditResyncAtRef = useRef(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const shell = sidebarShellRef.current;
      if (!shell) {
        return;
      }
      shell.setAttribute("data-sidebar-transition", "instant");
      const frame = requestAnimationFrame(() => {
        shell.setAttribute("data-sidebar-transition", "animated");
      });
      return () => cancelAnimationFrame(frame);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isWorkbenchRoute) {
    t2 = [isWorkbenchRoute];
    $[1] = isWorkbenchRoute;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useLayoutEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== fetchSharesForCurrentUser) {
    t3 = () => {
      fetchSharesForCurrentUser();
    };
    t4 = [fetchSharesForCurrentUser];
    $[3] = fetchSharesForCurrentUser;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== currentWorkspaceId || $[7] !== fetchBillingProfileStatus || $[8] !== fetchCreditBalance) {
    t5 = () => {
      if (!currentWorkspaceId) {
        return;
      }
      fetchCreditBalance(currentWorkspaceId);
      fetchBillingProfileStatus(currentWorkspaceId);
    };
    t6 = [currentWorkspaceId, fetchCreditBalance, fetchBillingProfileStatus];
    $[6] = currentWorkspaceId;
    $[7] = fetchBillingProfileStatus;
    $[8] = fetchCreditBalance;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] !== currentWorkspaceId || $[12] !== fetchCreditBalance) {
    t7 = () => {
      if (!currentWorkspaceId) {
        return;
      }
      const now = Date.now();
      if (now - lastCreditResyncAtRef.current < 1000) {
        return;
      }
      lastCreditResyncAtRef.current = now;
      fetchCreditBalance(currentWorkspaceId);
    };
    $[11] = currentWorkspaceId;
    $[12] = fetchCreditBalance;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const resyncCredits = t7;
  let t8;
  let t9;
  if ($[14] !== currentWorkspaceId || $[15] !== resyncCredits) {
    t8 = () => {
      if (!currentWorkspaceId) {
        return;
      }
      const onVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          resyncCredits();
        }
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
    };
    t9 = [currentWorkspaceId, resyncCredits];
    $[14] = currentWorkspaceId;
    $[15] = resyncCredits;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  useEffect(t8, t9);
  let t10;
  if ($[18] !== isWorkbenchRoute) {
    t10 = open => {
      if (isWorkbenchRoute) {
        setWorkbenchSidebarOpen(open);
      } else {
        setStandardSidebarOpen(open);
      }
    };
    $[18] = isWorkbenchRoute;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleSidebarOpenChange = t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <AppSidebar />;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = isWorkbenchRoute && "border-r border-sidebar-border";
  let t13;
  if ($[21] !== t12) {
    t13 = cn("min-h-0 overflow-hidden", t12);
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== children || $[24] !== t13) {
    t14 = <SidebarInset className={t13}>{children}</SidebarInset>;
    $[23] = children;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== showDeferredUi) {
    t15 = showDeferredUi ? <OutOfCreditsDialog /> : null;
    $[26] = showDeferredUi;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <LaunchHandoffRoot />;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== handleSidebarOpenChange || $[30] !== sidebarOpen || $[31] !== t14 || $[32] !== t15) {
    t17 = <SidebarProvider ref={sidebarShellRef} open={sidebarOpen} onOpenChange={handleSidebarOpenChange} defaultOpen={true} data-sidebar-transition="animated" className="overflow-hidden">{t11}{t14}{t15}{t16}</SidebarProvider>;
    $[29] = handleSidebarOpenChange;
    $[30] = sidebarOpen;
    $[31] = t14;
    $[32] = t15;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  return t17;
}
function _temp4(s_2) {
  return s_2.fetchBillingProfileStatus;
}
function _temp3(s_1) {
  return s_1.fetchBalance;
}
function _temp2(s_0) {
  return s_0.currentWorkspaceId;
}
function _temp(s) {
  return s.fetchSharesForCurrentUser;
}
export default function DashboardLayout(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex h-screen items-center justify-center"><div className="text-muted-foreground">Loading...</div></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <ProtectedRoute><SetupGate><Suspense fallback={t1}><DashboardShellContent>{children}</DashboardShellContent></Suspense></SetupGate></ProtectedRoute>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}